import React, { useState, useContext } from 'react';
import _ from 'lodash';

import { FormControls } from '@components/forms/formControls';
import { convertFilterToIds, getFilterOptionId, IFilterOption } from '@components/filters/filterLib';
import { useTranslation } from '@lib/useTypedTranslation';

import { FilterFormProps } from './popoverDialog';
import { FilterContext } from './context';

import { ALERT } from '@components/alertsPanel/alertMessages';
import { statusFilterValues, devicesFilterIcons } from '@components/filters/devicesFilterLib';

import './alertsFilter.css';

interface AlertTypeConfig<T extends string> {
  alertTypes?: readonly T[],
  alertNames?: Record<T, string>,
  getFilterId?: (alertType: T, alertLevel: ALERT) => string,
  getAvailableLevels?: (alertType: T) => ALERT[],
  statusHeaderLabel?: string
}

interface BaseAlertsFilterProps<T extends string> extends FilterFormProps {
  alertConfig: AlertTypeConfig<T>,
  className?: string
}

export function BaseAlertsFilter<T extends string>({ close, alertConfig, className }: BaseAlertsFilterProps<T>) {
  const { handleCancel, handleFiltersUpdated, filterOptions, filters } = useContext(FilterContext);
  const { t } = useTranslation(['translation', 'filters']);

  const { alertTypes, alertNames, getFilterId, getAvailableLevels, statusHeaderLabel } = alertConfig;

  const ids = convertFilterToIds(filters);

  const initialStatus = (Array.isArray(ids.status) ? ids.status : []) as string[];
  const initialAlerts = (Array.isArray(ids.alertStatus) ? ids.alertStatus : []) as string[];

  const [selectedStatus, setSelectedStatus] = useState<string[]>(initialStatus);
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>(initialAlerts);

  const isStatusSelected = (statusId: string) => selectedStatus.includes(statusId);
  const isAlertSelected = (alertId: string) => selectedAlerts.includes(alertId);

  const hasChanged = !_.isEqual(selectedStatus, initialStatus)
    || !_.isEqual(selectedAlerts, initialAlerts);


  const toggleStatus = (statusId: string) => {
    setSelectedStatus(prev =>
      prev.includes(statusId)
        ? prev.filter(id => id !== statusId)
        : [...prev, statusId]
    );
  };

  const toggleAlert = (alertType: T, alertLevel: ALERT) => {
    const alertId = getFilterId(alertType, alertLevel);

    setSelectedAlerts(prev =>
      prev.includes(alertId)
        ? prev.filter(id => id !== alertId)
        : [...prev, alertId]
    );
  };


  return (
    <div className={`alerts-filter ${className || ''}`} data-id="alerts-filter">
      <div className="filter-dialog_popover-content">
        <div className="alerts-filter_device-status">
          <table className="alerts-filter_status-table">
            <thead>
              <tr>
                <th></th>
                {statusFilterValues.map(status => (
                  <th key={status.id}>
                    <i className={devicesFilterIcons.status[status.id]} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{statusHeaderLabel || t('DEVICE_STATUS_HEADER', { ns: 'translation' })}</td>
                {statusFilterValues.map(status => (
                  <td key={status.id}>
                    <input
                      type="checkbox"
                      checked={isStatusSelected(status.id)}
                      onChange={() => toggleStatus(status.id)}
                      data-id={`${status.id}-status-option`}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        {alertTypes && (
          <>
            <h3>{t('ALERT_TYPES_FILTER', { ns: 'filters' })}</h3>
            <div className="alerts-filter_alert-types">
              <table className="alerts-filter_alert-types-table">

                <tbody>
                  {alertTypes.map(alertType => {
                    const alertName = alertNames![alertType];
                    const availableLevels = getAvailableLevels!(alertType);

                    return (
                      <tr key={alertType}>
                        <td className="alerts-filter_alert-type-name">{alertName}</td>
                        {statusFilterValues.map(status => {
                          const alertLevel = status.id as ALERT;
                          const isAvailable = availableLevels.includes(alertLevel);
                          const alertId = getFilterId!(alertType, alertLevel);

                          return (
                            <td key={status.id}>
                              <input
                                type="checkbox"
                                checked={isAvailable && isAlertSelected(alertId)}
                                disabled={!isAvailable}
                                onChange={() => toggleAlert(alertType, alertLevel)}
                                data-id={`${alertType}-${alertLevel}-option`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <FormControls
        hideStatus
        mode="apply"
        submitDisabled={!hasChanged}
        onCancel={() => {
          close();
          handleCancel();
        }}
        onSubmit={() => {
          close();

          const availableStatusOptions = filterOptions!.availableFilters!.status as IFilterOption[];
          const statusFilters = selectedStatus
            .map(id => availableStatusOptions.find(opt => getFilterOptionId(opt) === id))
            .filter(opt => opt !== undefined);

          const alertFilters = alertTypes
            ? selectedAlerts.map(id => (filterOptions!.availableFilters!.alertStatus as IFilterOption[]).find(opt => getFilterOptionId(opt) === id)).filter(opt => opt !== undefined)
            : undefined;

          handleFiltersUpdated({
            ...filters,
            status: statusFilters,
            ...(alertTypes && { alertStatus: alertFilters })
          });
        }}
      />
    </div>
  );
}
